/**
 * Advanced formatters for handling extreme number values
 * Handles very large numbers (billions/trillions) and very small decimal values
 */

import { getLocalizedAbbreviations } from './localeFormatters';

// Helper to determine if a number should use scientific notation
const shouldUseScientificNotation = (value: number): boolean => {
  // Use scientific notation for very small positive numbers or very large numbers
  return (value > 0 && value < 0.000001) || Math.abs(value) >= 1e12;
};

// Format numbers with abbreviations for large values (M, B, T)
export const formatWithAbbreviation = (value: number, locale: string = 'en-US'): string => {
  if (value === 0) return '0';
  
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  // Get localized abbreviations
  const { million, billion } = getLocalizedAbbreviations(locale);
  
  // For very small values, use adaptive precision
  if (abs > 0 && abs < 0.001) {
    // Show scientific notation for extremely small values
    return formatScientificNotation(value);
  }
  
  // For large values, use localized abbreviations (only for millions and up)
  if (abs >= 1e12) return formatScientificNotation(value); // Use scientific notation for T+
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)} ${billion}`; // Billions
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)} ${million}`; // Millions
  
  // For values below 1 million, use the appropriate precision
  return formatAdaptivePrecision(value, locale);
};

// Format with scientific notation (for very small or very large numbers)
const formatScientificNotation = (value: number): string => {
  if (value === 0) return '0';
  
  // For very small positive numbers (like those in scientific notation with negative exponents)
  const abs = Math.abs(value);
  if (abs > 0 && abs < 0.001) {
    return '0,00...';
  }
  
  // Format with scientific notation with 3 significant digits
  return value.toExponential(2);
};

// Format with adaptive precision based on the value's magnitude
const formatAdaptivePrecision = (value: number, locale: string = 'en-US'): string => {
  const abs = Math.abs(value);
  
  if (abs === 0) return '0';
  
  // For values between 0.001 and 0.01
  if (abs > 0 && abs < 0.01) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 6,
      maximumFractionDigits: 8,
    }).format(value);
  }
  
  // For values between 0.01 and 1
  if (abs >= 0.01 && abs < 1) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    }).format(value);
  }
  
  // For values between 1 and 1000
  if (abs >= 1 && abs < 1000) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  }
  
  // For larger values (though these should be handled by formatWithAbbreviation)
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

// Format quantity smartly - more decimals for small values, abbreviations for large values
export const formatQuantity = (value: number, locale: string = 'en-US'): string => {
  return formatWithAbbreviation(value, locale);
};

// Format price smartly - correct precision for different scales
export const formatPrice = (value: number, currency: string = 'USD', locale: string = 'en-US'): string => {
  // For very small numbers, display with ellipsis
  if (Math.abs(value) > 0 && Math.abs(value) < 0.000001) {
    // Format as currency with ellipsis
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0) + '...';
  }
  
  if (shouldUseScientificNotation(value)) {
    return formatScientificNotation(value);
  }
  
  // Always display with exactly 2 decimal places for UI consistency
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Format with tooltip data - returns display value and full precision value for tooltip
export const formatWithTooltip = (value: number, locale: string = 'en-US'): { display: string; tooltip: string } => {
  // Format the tooltip with high precision for reference
  const tooltipFormat = new Intl.NumberFormat(locale, {
    maximumSignificantDigits: 10,
    useGrouping: true,
  });
  
  return {
    display: formatWithAbbreviation(value),
    tooltip: tooltipFormat.format(value)
  };
};
